import { useRef, Fragment, useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { IconBell } from '@tabler/icons-react';

import NotificationDropdown from '@/components/NotificationDropdown';
import Select from '@/components/Select';
import { LANGUAGE_OPTIONS } from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { getWebNotificationList } from '@/service/notificationService';
import { updateLanguageRegion } from '@/service/settingsService';
import { useSettingsStore } from '@/store/useSettingsStore';

const Header = () => {
  const { breadcrumb, title } = usePageTitle();
  const settings = useSettingsStore((s) => s.settings);
  const updateSettings = useSettingsStore((s) => s.updateSettings);
  const { formatText } = useFormatText();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();

  // Sync current language to service worker for push notification translations
  useEffect(() => {
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SET_LANGUAGE',
        language: settings.language,
      });
    }

    // Listen for the service worker asking for the current language (e.g. after SW restart)
    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.type === 'GET_LANGUAGE' && event.ports[0]) {
        event.ports[0].postMessage(settings.language);
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleSWMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, [settings.language]);

  // Fetch unread notification count on mount (first login) and on every route change
  const fetchUnreadCount = useCallback(async () => {
    const response = await getWebNotificationList({ limit: 1, offset: 0 });
    if (response?.status) {
      setUnreadCount(response.unread_count || 0);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
  }, [location.pathname, fetchUnreadCount]);

  const handleLanguageChange = async (value: string | string[]) => {
    const langValue = Array.isArray(value) ? value[0] : (value as string);
    const newLang = langValue as 'en' | 'ar';
    updateSettings({
      language: newLang,
      direction: newLang === 'ar' ? 'rtl' : 'ltr',
    });

    await updateLanguageRegion({
      language: newLang,
      date_format: settings.dateFormat,
      currency: settings.currency,
    });
  };

  return (
    <header className="flex flex-wrap items-center justify-between bg-white gap-4">
      <div className="flex items-center gap-2">
        {breadcrumb.length > 0 && (
          <div className="flex items-center gap-3 text-sm text-[#6B7280] font-medium">
            {breadcrumb.map((item, index) => (
              <Fragment key={index}>
                {item.to ? (
                  <Link
                    to={item.to}
                    state={item.state}
                    className="hover:text-neutral-secondary text-sm font-semibold transition-colors cursor-pointer"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span
                    className={
                      index === breadcrumb.length - 1
                        ? 'text-sm font-semibold leading-[150%] text-neutral-primary'
                        : ''
                    }
                  >
                    {item.label}
                  </span>
                )}
                {index < breadcrumb.length - 1 && (
                  <div className="w-1 h-1 rounded-full bg-current" />
                )}
              </Fragment>
            ))}
          </div>
        )}
        {breadcrumb.length === 0 && (
          <p className="font-bold text-base leading-[150%] text-neutral-primary">
            {title}
          </p>
        )}
      </div>

      <div className="flex items-center gap-4">
        <Select
          placeholder={formatText('header.language')}
          options={LANGUAGE_OPTIONS}
          selected={settings.language}
          onChange={handleLanguageChange}
          className="w-37.5! py-2.25"
        />
        <div
          ref={bellRef}
          onClick={() => setIsNotificationOpen((prev) => !prev)}
          className="relative p-2 h-10 w-10 border border-neutral-light-200 shadow-small text-neutral-500 rounded-lg cursor-pointer hover:bg-neutral-50 transition"
        >
          <IconBell size={20} color="#717784" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-error-secondary text-white text-[10px] font-semibold leading-none">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>
        <NotificationDropdown
          isOpen={isNotificationOpen}
          onClose={() => setIsNotificationOpen(false)}
          anchorRef={bellRef}
          onUnreadCountChange={setUnreadCount}
        />
      </div>
    </header>
  );
};

export default Header;
